import React, { useEffect, useMemo } from "react";
import { useCarsContext } from "../context/CarsContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, LineChart, Line, CartesianGrid,
} from "recharts";
import "./Analytics.css";

const COLORS = ["#eb0a1e", "#2b2d33", "#6c757d", "#ffb703", "#1a73e8"];
const ACCENT = "#eb0a1e";

const formatPrice = (n) =>
  "$ " + Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="analytics-tooltip">
        <p className="tooltip-label">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color || ACCENT }}>
            {typeof p.value === "number" && p.value > 1000
              ? formatPrice(p.value)
              : p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const Analytics = () => {
  const { cars, loading, fetchCarsData } = useCarsContext() || {};

  useEffect(() => {
    if (fetchCarsData) fetchCarsData(null, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const data = useMemo(() => cars || [], [cars]);

  // ── KPIs ──
  const kpis = useMemo(() => {
    if (!data.length) return null;
    const prices = data.map((c) => c.price);
    const years = data.map((c) => c.year);
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const segments = new Set(data.map((c) => c.segment)).size;
    return {
      total: data.length,
      avgPrice,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      newestYear: Math.max(...years),
      segments,
    };
  }, [data]);

  // ── Distribución por segmento ──
  const segmentData = useMemo(() => {
    const counts = {};
    data.forEach((c) => {
      const label = c.segment || "Otro";
      counts[label] = (counts[label] || 0) + 1;
    });
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [data]);

  // ── Precio promedio por segmento ──
  const avgPriceBySegment = useMemo(() => {
    const grouped = {};
    data.forEach((c) => {
      const label = c.segment || "Otro";
      if (!grouped[label]) grouped[label] = [];
      grouped[label].push(c.price);
    });
    return Object.entries(grouped).map(([name, prices]) => ({
      name,
      avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
    }));
  }, [data]);

  // ── Precio promedio por año ──
  const avgPriceByYear = useMemo(() => {
    const grouped = {};
    data.forEach((c) => {
      if (!grouped[c.year]) grouped[c.year] = [];
      grouped[c.year].push(c.price);
    });
    return Object.entries(grouped)
      .sort((a, b) => a[0] - b[0])
      .map(([year, prices]) => ({
        year,
        avg: Math.round(prices.reduce((a, b) => a + b, 0) / prices.length),
      }));
  }, [data]);

  // ── Ranking: mejor relación año/precio (más nuevo y más barato relativo a su segmento) ──
  const bestValue = useMemo(() => {
    const segAvg = {};
    avgPriceBySegment.forEach((s) => { segAvg[s.name] = s.avg; });

    return [...data]
      .map((c) => {
        const label = c.segment || "Otro";
        const diff = segAvg[label] ? ((segAvg[label] - c.price) / segAvg[label]) * 100 : 0;
        return { ...c, segmentLabel: label, diffPct: diff };
      })
      .sort((a, b) => b.diffPct - a.diffPct)
      .slice(0, 5);
  }, [data, avgPriceBySegment]);

  // ── Top 5 más caros / más baratos ──
  const sortedByPrice = useMemo(
    () => [...data].sort((a, b) => b.price - a.price),
    [data]
  );
  const top5Expensive = sortedByPrice.slice(0, 5);
  const top5Cheap = [...sortedByPrice].reverse().slice(0, 5);

  if (loading && data.length === 0) {
    return (
      <div className="analytics container my-4">
        <p>Cargando datos del inventario...</p>
      </div>
    );
  }

  if (!kpis) {
    return (
      <div className="analytics container my-4">
        <p>No hay datos disponibles.</p>
      </div>
    );
  }

  return (
    <div className="analytics container my-4">
      <div className="analytics-header">
        <h1>Análisis de Inventario</h1>
        <p className="analytics-subtitle">
          Estadísticas sobre {kpis.total} modelos disponibles
        </p>
      </div>

      {/* KPIs */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <span className="kpi-value">{kpis.total}</span>
          <span className="kpi-label">Modelos en catálogo</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{formatPrice(kpis.avgPrice)}</span>
          <span className="kpi-label">Precio promedio</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{formatPrice(kpis.minPrice)}</span>
          <span className="kpi-label">Precio más bajo</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{formatPrice(kpis.maxPrice)}</span>
          <span className="kpi-label">Precio más alto</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{kpis.newestYear}</span>
          <span className="kpi-label">Año más reciente</span>
        </div>
        <div className="kpi-card">
          <span className="kpi-value">{kpis.segments}</span>
          <span className="kpi-label">Segmentos distintos</span>
        </div>
      </div>

      {/* Grid de gráficos */}
      <div className="chart-grid">
        {/* Distribución por segmento */}
        <div className="chart-card chart-md">
          <h3>Modelos por segmento</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie
                data={segmentData}
                cx="50%"
                cy="50%"
                innerRadius={55}
                outerRadius={85}
                dataKey="value"
                paddingAngle={3}
                strokeWidth={0}
              >
                {segmentData.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                formatter={(v) => <span className="legend-text">{v}</span>}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Precio promedio por segmento */}
        <div className="chart-card chart-md">
          <h3>Precio promedio por segmento</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={avgPriceBySegment} margin={{ left: 10 }}>
              <XAxis
                dataKey="name"
                tick={{ fontSize: 11 }}
                interval={0}
                angle={-15}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="avg" radius={[4, 4, 0, 0]}>
                {avgPriceBySegment.map((_, i) => (
                  <Cell key={i} fill={COLORS[i % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Evolución de precio por año */}
        <div className="chart-card chart-lg">
          <h3>Precio promedio por año</h3>
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={avgPriceByYear}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.06)" />
              <XAxis dataKey="year" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 11 }}
                tickFormatter={(v) => `$${(v / 1000000).toFixed(0)}M`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Line
                type="monotone"
                dataKey="avg"
                stroke={ACCENT}
                strokeWidth={3}
                dot={{ r: 5, fill: ACCENT }}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Top 5 más caros */}
        <div className="chart-card chart-sm">
          <h3>Top 5 más caros</h3>
          <ul className="rank-list">
            {top5Expensive.map((c, i) => (
              <li key={c.id} className="rank-item">
                <span className="rank-num">{i + 1}</span>
                <span className="rank-name">{c.name}</span>
                <span className="rank-value">{formatPrice(c.price)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Top 5 más baratos */}
        <div className="chart-card chart-sm">
          <h3>Top 5 más accesibles</h3>
          <ul className="rank-list">
            {top5Cheap.map((c, i) => (
              <li key={c.id} className="rank-item">
                <span className="rank-num">{i + 1}</span>
                <span className="rank-name">{c.name}</span>
                <span className="rank-value">{formatPrice(c.price)}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Mejor relación precio/segmento */}
        <div className="chart-card chart-lg">
          <h3>Mejor relación precio dentro de su segmento</h3>
          <p className="chart-desc">
            Modelos con el precio más conveniente comparado contra el promedio de su propio segmento
          </p>
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Modelo</th>
                  <th>Segmento</th>
                  <th>Año</th>
                  <th>Precio</th>
                  <th>Vs. promedio</th>
                </tr>
              </thead>
              <tbody>
                {bestValue.map((c) => (
                  <tr key={c.id}>
                    <td className="td-name">{c.name}</td>
                    <td>{c.segmentLabel}</td>
                    <td>{c.year}</td>
                    <td>{formatPrice(c.price)}</td>
                    <td>
                      <span
                        className={`diff-badge ${
                          c.diffPct >= 0 ? "diff-positive" : "diff-negative"
                        }`}
                      >
                        {c.diffPct >= 0 ? "▼" : "▲"} {Math.abs(c.diffPct).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
